import { alpha, Grid, List, ListItem, ListItemText, Stack, Typography, useTheme } from '@mui/material';
import { FileUp, ImageUp } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { DropzoneOptions, useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

import { Loader } from '~base/ui/Loader';
import FileHelper from '~helpers/FileHelper';
import { AttachedFilesStore } from '~modules/attachedFiles/AttachedFilesStore';
import { FileFormatModel } from '~modules/fileTypes/models/FileFormatModel';
import { FixedTypeModel } from '~modules/fileTypes/models/FixedTypeModel';
import { FileModeEnum } from '~modules/fileTypes/types/FileModeEnum';
import { useCommonStyles } from '~styles/commonStyles';

interface IFileDropzoneProps {
  options?: Omit<DropzoneOptions, 'onDrop' | 'validator' | 'maxFiles'>;
  error?: boolean;
  disabled?: boolean;
  maxFiles: number;
  attachedFilesStore: AttachedFilesStore;
  /**
   * Тип файлов, с которыми работает компонент - любые, изображения, документы
   */
  fileMode: FileModeEnum;
  /**
   * ID раздела, в котором используется компонент, например "Маркетинговые акции"
   */
  documentTypeId: number;
  /**
   * ID "карточки", обычно берется из URL
   */
  documentId: number | undefined;
  /**
   * Строковый ID, например артикул детали
   */
  documentTitle: string | undefined;

  /**
   * Тип файла
   */
  fixedType?: FixedTypeModel | null;

  onUploadFiles: () => void;

  required?: boolean;

  fileFormats: FileFormatModel[];

  withoutGroup?: boolean;

  notShowFixTypeLabels?: boolean;
}

export const FileDropzone: React.FC<IFileDropzoneProps> = observer((props) => {
  const {
    attachedFilesStore,
    onUploadFiles,
    options,
    error,
    disabled,
    maxFiles,
    fileMode,
    documentTypeId,
    documentId,
    documentTitle,
    fixedType,
    required,
    fileFormats,
    notShowFixTypeLabels,
  } = props;

  const { classes, cx } = useStyles();
  const { classes: commonClasses } = useCommonStyles();

  const { palette } = useTheme();

  const { t } = useTranslation();

  const acceptFormats = FileHelper.generateAcceptFormats(fileFormats);

  const { isDragActive, getRootProps, getInputProps, fileRejections } = useDropzone({
    ...options,
    maxFiles: maxFiles,
    accept: acceptFormats,
    disabled: disabled || attachedFilesStore.uploadedFilesLoading,
    onDrop: (acceptedFiles: File[]) => {
      attachedFilesStore.uploadFiles(
        documentTypeId,
        documentId,
        documentTitle,
        fixedType?.id,
        acceptedFiles,
        onUploadFiles,
      );
    },
    validator: FileHelper.generateFileDropzoneAcceptFormats(fileFormats, t),
  });

  // Effects

  // Handlers

  // Render

  const renderDropzoneIcon = () => {
    if (attachedFilesStore.uploadedFilesLoading) {
      return <Loader size={24} />;
    }

    if (fileMode === FileModeEnum.IMAGES_ONLY) {
      return <ImageUp size={24} />;
    }

    return <FileUp size={24} />;
  };

  const renderErrors = () => {
    const hasErrors = error || fileRejections.some(({ errors }) => errors.length > 0);

    if (!hasErrors) {
      return null;
    }

    const errorMessages = FileHelper.processFileErrors(fileRejections, t, maxFiles);

    return (
      <List dense>
        {error && (
          <ListItem disableGutters disablePadding>
            <ListItemText sx={{ color: palette.error.main }}>{t('error.somethingWrong')}</ListItemText>
          </ListItem>
        )}

        {errorMessages.map((message, index) => (
          <ListItem key={`${index}_${message}`} disableGutters disablePadding>
            <ListItemText sx={{ color: palette.error.main }}>{message}</ListItemText>
          </ListItem>
        ))}
      </List>
    );
  };

  const renderGroupName = () => {
    if (fileMode !== FileModeEnum.IMAGES_EXCLUDED || notShowFixTypeLabels) {
      return;
    }

    return (
      <Typography variant="caption" sx={{ color: 'inherit' }}>
        {fixedType?.descr}
      </Typography>
    );
  };

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <div
          {...getRootProps({
            className: cx(commonClasses.fileItem, classes.dropzone, {
              isDragActive,
              disabled: disabled || attachedFilesStore.uploadedFilesLoading,
              error: error,
              required: required,
            }),
          })}
        >
          <input {...getInputProps()} />
          <Stack direction="column" alignItems="center" gap={0.5}>
            {renderDropzoneIcon()}
            {renderGroupName()}
          </Stack>
        </div>
      </Grid>
      <Grid item xs={12}>
        {renderErrors()}
      </Grid>
    </Grid>
  );
});

const useStyles = makeStyles()((theme) => ({
  dropzone: {
    color: alpha(theme.palette.primary.main, 0.5),
    transition: theme.transitions.create('color'),
    '&:hover:not(.disabled), &.isDragActive': {
      borderColor: alpha(theme.palette.primary.main, 1),
      color: alpha(theme.palette.primary.main, 1),
      '& .lucide': {
        color: alpha(theme.palette.primary.main, 1),
      },
    },
    '&:hover:not(.disabled)': {
      cursor: 'pointer',
    },
    '& .lucide': {
      color: alpha(theme.palette.primary.main, 0.5),
      transition: theme.transitions.create('color'),
    },
    '&.error': {
      borderColor: theme.palette.error.light,
    },
    '&.required': {
      borderColor: alpha(theme.palette.error.main, 0.5),
      color: alpha(theme.palette.error.main, 0.5),
      '&:hover:not(.disabled), &.isDragActive': {
        borderColor: alpha(theme.palette.error.main, 1),
        color: alpha(theme.palette.error.main, 1),
        '& .lucide': {
          color: alpha(theme.palette.error.main, 1),
        },
      },
      '& .lucide': {
        color: alpha(theme.palette.error.main, 0.5),
      },
    },
    width: '100%',
  },
}));
